import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, RotateCcw, X, Check, SwitchCamera, ImageIcon, AlertTriangle } from "lucide-react";

function detectError(e) {
  const name = e?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") return {
    title: "Camera access was denied",
    msg: "Please allow camera access in your browser settings, then try again.",
  };
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return {
    title: "No camera found",
    msg: "This device does not appear to have a camera available.",
  };
  if (name === "NotReadableError" || name === "TrackStartError") return {
    title: "Camera is in use",
    msg: "Another app is using the camera. Close it and try again.",
  };
  if (name === "OverconstrainedError") return {
    title: "Camera not compatible",
    msg: "The selected camera mode isn't supported. Try switching cameras.",
  };
  if (name === "SecurityError" || name === "TypeError") return {
    title: "Camera blocked by browser",
    msg: "Camera access requires a secure (HTTPS) connection.",
  };
  return { title: "Camera unavailable", msg: e?.message || "Unable to start camera. Please try again or choose a photo from your gallery." };
}

export default function CameraCapture({ onClose, onCapture, onFallback }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [photo, setPhoto] = useState(null); // {blob, url}
  const [err, setErr] = useState(null);
  const [ready, setReady] = useState(false);
  const [starting, setStarting] = useState(true);

  const stopStream = useCallback(() => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const start = useCallback(async (mode) => {
    setStarting(true); setReady(false); setErr(null);
    stopStream();
    if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErr({ title: "Camera not supported", msg: "This browser does not support camera access. Please choose a photo from your gallery." });
      setStarting(false);
      return;
    }
    try {
      const constraints = { video: { facingMode: { ideal: mode } }, audio: false };
      let s;
      try {
        s = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e1) {
        // Retry without facingMode constraint (some desktops fail with facingMode)
        if (e1?.name === "OverconstrainedError" || e1?.name === "NotFoundError") {
          s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } else {
          throw e1;
        }
      }
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.setAttribute("playsinline", "true");
        try { await videoRef.current.play(); } catch {}
      }
      setReady(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Camera start failed:", e);
      setErr(detectError(e));
    } finally {
      setStarting(false);
    }
  }, [stopStream]);

  useEffect(() => {
    start(facing);
    return () => stopStream();
  }, [facing, start, stopStream]);

  const shoot = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => setPhoto({ blob, url: URL.createObjectURL(blob) }), "image/jpeg", 0.92);
  };

  const accept = () => {
    if (!photo) return;
    const file = new File([photo.blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
    stopStream();
    onCapture(file, "camera");
    URL.revokeObjectURL(photo.url);
  };

  const handleClose = () => { stopStream(); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] bg-black" data-testid="camera-modal">
      <div className="absolute inset-0 flex flex-col">
        <div className="p-4 flex justify-between items-center text-white">
          <button onClick={handleClose} className="glass-subtle rounded-full p-2" data-testid="camera-close"><X className="w-5 h-5" /></button>
          <div className="text-xs uppercase tracking-widest opacity-80">Take a photo</div>
          <button onClick={() => setFacing(f => f === "environment" ? "user" : "environment")} disabled={!ready} className="glass-subtle rounded-full p-2 disabled:opacity-40" data-testid="camera-switch"><SwitchCamera className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 relative overflow-hidden grid place-items-center">
          {starting && !err && (
            <div className="text-white/80 text-sm animate-pulse">Requesting camera…</div>
          )}
          {err && (
            <div className="glass p-6 max-w-sm mx-4 text-center" data-testid="camera-error">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--accent)" }} />
              <div className="font-serif text-2xl mb-2">{err.title}</div>
              <p className="text-neutral-700 text-sm">{err.msg}</p>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                <button onClick={() => start(facing)} className="rounded-full px-5 py-2 text-sm text-white" style={{ background: "var(--accent)" }} data-testid="camera-try-again">Try Again</button>
                {onFallback && (
                  <button onClick={() => { stopStream(); onFallback(); }} className="btn-ghost-glass rounded-full px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="camera-fallback">
                    <ImageIcon className="w-4 h-4" /> Choose from Gallery
                  </button>
                )}
                <button onClick={handleClose} className="btn-ghost-glass rounded-full px-5 py-2 text-sm" data-testid="camera-error-close">Close</button>
              </div>
            </div>
          )}
          {!err && !photo && (
            <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" data-testid="camera-video" />
          )}
          {photo && (
            <img src={photo.url} alt="preview" className="w-full h-full object-contain" data-testid="camera-preview-img" />
          )}
        </div>
        {!err && (
          <div className="p-6 flex justify-center items-center gap-6 bg-black/60">
            {!photo ? (
              <button onClick={shoot} disabled={!ready} className="w-20 h-20 rounded-full border-4 border-white bg-white/95 grid place-items-center disabled:opacity-40" data-testid="camera-shoot">
                <div className="w-14 h-14 rounded-full bg-white shadow-inner" />
              </button>
            ) : (
              <>
                <button onClick={() => { URL.revokeObjectURL(photo.url); setPhoto(null); }} className="glass-subtle rounded-full px-5 py-3 text-white flex items-center gap-2" data-testid="camera-retake"><RotateCcw className="w-4 h-4" /> Retake</button>
                <button onClick={accept} className="rounded-full px-6 py-3 text-white flex items-center gap-2" style={{ background: "var(--accent)" }} data-testid="camera-accept"><Check className="w-4 h-4" /> Use photo</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
